import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes,
  faStar
} from '@fortawesome/free-solid-svg-icons';
import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';

// ========== TRANSLATIONS ==========
const translations = {
  en: {
    movies: 'Movies',
    tvShows: 'TV Shows',
    filmography: 'Filmography',
    loadingFilmography: 'Loading filmography...',
    noFilmography: 'No {contentType} found with posters and at least 80 votes',
    noData: 'No filmography data available',
  },
  fr: {
    movies: 'Films',
    tvShows: 'Séries TV',
    filmography: 'Filmographie',
    loadingFilmography: 'Chargement de la filmographie...',
    noFilmography: 'Aucun {contentType} trouvé avec des affiches et au moins 80 votes',
    noData: 'Aucune donnée de filmographie disponible',
  },
  es: {
    movies: 'Películas',
    tvShows: 'Series de TV',
    filmography: 'Filmografía',
    loadingFilmography: 'Cargando filmografía...',
    noFilmography: 'No se encontraron {contentType} con pósters y al menos 80 votos',
    noData: 'No hay datos de filmografía disponibles',
  },
  it: {
    movies: 'Film',
    tvShows: 'Serie TV',
    filmography: 'Filmografia',
    loadingFilmography: 'Caricamento filmografia...',
    noFilmography: 'Nessun {contentType} trovato con locandine e almeno 80 voti',
    noData: 'Nessun dato filmografico disponibile',
  },
  nl: {
    movies: 'Films',
    tvShows: 'Series',
    filmography: 'Filmografie',
    loadingFilmography: 'Filmografie laden...',
    noFilmography: 'Geen {contentType} gevonden met posters en minimaal 80 stemmen',
    noData: 'Geen filmografiegegevens beschikbaar',
  },
};

const ActorModal = ({
  actor,
  onClose,
  isMobile,
  onContentClick,
  history,
  predefinedBackdrops,
  getHighResImage,
  actorFallbackImage,
  TMDB_API_KEY,
  ratingFormat = 'stars'
}) => {
  const [contentFilter, setContentFilter] = useState('movies');
  const [actorFilmography, setActorFilmography] = useState(null);
  const [filmographyLoading, setFilmographyLoading] = useState(false);
  const [actorBackdropUrl, setActorBackdropUrl] = useState(null);
  const [backdropLoaded, setBackdropLoaded] = useState(false);
  
  const actorModalRef = useRef(null);
  const loadingTimeoutRef = useRef(null);

  // Language state
  const [lang, setLang] = useState(() => {
    return localStorage.getItem('preferredLanguage') || 'en';
  });

  const t = (key) => {
    const langData = translations[lang] || translations.en;
    return langData[key] || key;
  };

  // Helper for dynamic messages with placeholder
  const tDynamic = (key, placeholder, value) => {
    const langData = translations[lang] || translations.en;
    return langData[key]?.replace(placeholder, value) || key;
  };

  // Listen to language changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'preferredLanguage') {
        setLang(e.newValue || 'en');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    if (!actor) {
      return;
    }
    
    // Set a random backdrop when actor is selected
    const randomBackdrop = predefinedBackdrops[Math.floor(Math.random() * predefinedBackdrops.length)];
    setActorBackdropUrl(randomBackdrop);
    
    // Preload the backdrop image
    const img = new Image();
    img.onload = () => {
      setBackdropLoaded(true);
    };
    img.src = randomBackdrop;
    
    // Fetch filmography for the actor
    const fetchFilmography = async () => {
      setFilmographyLoading(true);
      try {
        const response = await fetch(
          `https://api.themoviedb.org/3/person/${actor.id}/combined_credits?api_key=${TMDB_API_KEY}&language=en-US`
        );
        const data = await response.json();
        setActorFilmography(data);
      } catch (error) {
        console.error('Error fetching actor filmography:', error);
      } finally {
        // Delay hiding the loader to ensure the grid is fully rendered
        loadingTimeoutRef.current = setTimeout(() => {
          setFilmographyLoading(false);
        }, 300);
      }
    };

    fetchFilmography();

    // Cleanup timeout if component unmounts
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [actor, TMDB_API_KEY, predefinedBackdrops]);

  useEffect(() => {
    if (actorModalRef.current) {
      actorModalRef.current.scrollTop = 0;
    }
  }, [contentFilter]);

  // ---- Rating functions (match MovieCard / SeriesCard) ----
  const createStarRating = (rating, size = 'small') => {
    const starRating = rating / 2;
    const fullStars = Math.floor(starRating);
    const partialStar = starRating - fullStars;
    
    let stars = [];
    const starSize = size === 'small' ? '12px' : '19px';
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <div className="star" key={`full-${i}`} style={{ fontSize: starSize }}>
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: '100%' }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      );
    }
    
    if (partialStar > 0) {
      stars.push(
        <div className="star" key="partial" style={{ fontSize: starSize }}>
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: `${partialStar * 100}%` }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      );
    }
    
    const emptyStars = 5 - Math.ceil(starRating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <div className="star" key={`empty-${i}`} style={{ fontSize: starSize }}>
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: '0%' }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      );
    }
    
    return stars;
  };

  const formatRatingNumber = (rating) => {
    if (!rating || rating === 0) return null;
    return rating.toFixed(1);
  };

  // ---- Render rating based on format ----
  const renderFilmographyRating = (voteAverage) => {
    if (ratingFormat === 'stars') {
      return (
        <div className="filmography-rating-wrapper">
          {createStarRating(voteAverage || 0, 'small')}
        </div>
      );
    } else {
      const number = formatRatingNumber(voteAverage);
      return (
        <div className="filmography-rating-wrapper numeric">
          <span className="rating-star"><FontAwesomeIcon icon={faStar} /></span>
          <span className="rating-number">{number}</span>
        </div>
      );
    }
  };

  const filteredFilmography = () => {
    if (!actorFilmography) return [];
    
    // Use a Map to ensure each movie/TV show only appears once
    const uniqueItemsMap = new Map();
    
    actorFilmography.cast.forEach(item => {
      if (
        (contentFilter === 'movies' && 
         item.media_type === 'movie' && 
         item.poster_path &&
         (item.vote_count || 0) >= 80) ||
        (contentFilter === 'tv' && 
         item.media_type === 'tv' && 
         item.poster_path &&
         (item.vote_count || 0) >= 80 &&
         !item.genre_ids?.some(id => [10763, 10767, 10764].includes(id)))
      ) {
        // Use the item ID as the key to prevent duplicates
        if (!uniqueItemsMap.has(item.id)) {
          uniqueItemsMap.set(item.id, item);
        }
      }
    });
    
    const filtered = Array.from(uniqueItemsMap.values());
    
    filtered.sort((a, b) => {
      const dateA = a.release_date || a.first_air_date;
      const dateB = b.release_date || b.first_air_date;
      
      if (dateA && dateB) {
        return new Date(dateB) - new Date(dateA);
      }
      if (dateA && !dateB) return -1;
      if (!dateA && dateB) return 1;
      return 0;
    });
    
    return filtered;
  };

  // Get translated content type for empty message
  const getContentTypeLabel = () => {
    return contentFilter === 'movies' ? t('movies') : t('tvShows');
  };

  return (
    <div className="actor-modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'auto',
      touchAction: 'none',
      overscrollBehavior: 'contain',
      backgroundColor: '#000'
    }}>
      {/* Backdrop Layer - Only show when loaded */}
      {actorBackdropUrl && backdropLoaded && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${actorBackdropUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(8px) brightness(0.65)',
          zIndex: -2,
          transition: 'opacity 0.3s ease'
        }}></div>
      )}
      
      {/* Dark Overlay */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        zIndex: -1
      }}></div>
      
      <div 
        className="actor-modal" 
        ref={actorModalRef}
        style={{
          width: isMobile ? '100%' : '80%',
          maxWidth: isMobile ? 'none' : '1200px',
          backgroundColor: 'transparent',
          borderRadius: isMobile ? '0' : '10px',
          padding: isMobile ? '15px' : '30px',
          position: 'relative',
          maxHeight: isMobile ? '100vh' : '90vh',
          overflowY: 'auto',
          color: 'white',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          overscrollBehavior: 'contain'
        }}
      >
        <style>
          {`
            .actor-modal::-webkit-scrollbar {
              display: none;
            }
            /* ---- Filmography card styles (matching MovieCard / SeriesCard) ---- */
            .filmography-card {
              display: flex;
              flex-direction: column;
              background: transparent;
              border-radius: 8px;
              overflow: hidden;
              cursor: pointer;
              transition: transform 0.2s;
            }
            .filmography-card:hover .filmography-poster {
              transform: scale(1.05);
            }
            .filmography-poster-container {
              position: relative;
              overflow: hidden;
              background: #1a1a1a;
            }
            .filmography-poster {
              width: 100%;
              display: block;
              transition: transform 0.3s ease;
            }
            .filmography-details {
              padding: 8px 0px !important;
              text-align: left !important;
            }
            .filmography-title {
              font-weight: 600;
              font-size: clamp(12px, 1.1vw, 16px);
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              color: #fff;
              letter-spacing: 0.3px;
              padding: 0 2px;
            }
            .filmography-meta-row {
              display: flex !important;
              align-items: center !important;
              justify-content: space-between !important;
              width: 100%;
              gap: 6px;
              padding: 2px 2px 0;
            }
            .filmography-rating-wrapper {
              display: flex;
              align-items: center;
              flex-shrink: 0;
              margin: 0;
              padding: 0;
            }
            .filmography-rating-wrapper .star-rating {
              display: flex;
              align-items: center;
              gap: 1px;
              margin: 0;
              padding: 0;
            }
            .filmography-rating-wrapper .star {
              position: relative;
              display: inline-block;
              font-size: clamp(10px, 0.8vw, 14px);
              color: #555;
              line-height: 1;
              margin: 0;
              padding: 0;
            }
            .filmography-rating-wrapper .star .star-filled {
              position: absolute;
              top: 0;
              left: 0;
              overflow: hidden;
              color: #59f001;
            }
            .filmography-rating-wrapper .star .far.fa-star {
              color: #444;
            }
            /* Numeric rating */
            .filmography-rating-wrapper.numeric {
              gap: 2px;
              margin-left: 2px;
            }
            .filmography-rating-wrapper.numeric .rating-star {
              font-size: clamp(10px, 0.77vw, 13.2px);
              color: #59f001;
              line-height: 1;
            }
            .filmography-rating-wrapper.numeric .rating-number {
              font-size: clamp(10px, 0.77vw, 13.2px);
              color: #59f001;
              font-weight: 600;
            }
            .filmography-meta-year {
              font-size: clamp(10px, 0.77vw, 13.2px);
              color: rgba(255, 255, 255, 0.7);
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              flex: 1 1 auto;
              min-width: 0;
              text-align: right;
            }
            /* Mobile adjustments */
            @media (max-width: 768px) {
              .filmography-details {
                padding: 4px 0px !important;
              }
              .filmography-title {
                font-size: 11px;
              }
              .filmography-meta-row {
                padding-left: 0 !important;
              }
              .filmography-rating-wrapper {
                scale: 1 !important;
                margin: 0 !important;
                padding: 0 !important;
              }
              .filmography-rating-wrapper .star {
                font-size: 16px;
              }
              .filmography-meta-year {
                font-size: 13px;
              }
            }
            @media (max-width: 480px) {
              .filmography-title {
                font-size: 10px;
              }
              .filmography-rating-wrapper .star {
                font-size: 10px;
              }
              .filmography-meta-year {
                font-size: 10px;
              }
            }
            @media (max-width: 430px) {
              .filmography-title {
                font-size: 10px;
              }
              .filmography-rating-wrapper .star {
                font-size: 10px;
              }
              .filmography-meta-year {
                font-size: 10px;
              }
            }
          `}
        </style>
        
        <div className="close-actor-modal" onClick={onClose} style={{
          position: 'absolute',
          top: isMobile ? '15px' : '20px',
          right: isMobile ? '15px' : '20px',
          background: 'rgba(255, 255, 255, 0.15)',
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          zIndex: 10,
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <FontAwesomeIcon icon={faTimes} style={{ color: 'white', fontSize: '18px' }} />
        </div>
        
        <div className="actor-header" style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          marginBottom: '30px',
          textAlign: isMobile ? 'center' : 'left'
        }}>
          <div style={{
            position: 'relative',
            width: isMobile ? '120px' : '150px',
            height: isMobile ? '120px' : '150px',
            borderRadius: '50%',
            overflow: 'hidden',
            marginBottom: isMobile ? '15px' : 0,
            marginRight: isMobile ? 0 : '30px',
            border: '3px solid rgba(255, 255, 255, 0.2)'
          }}>
            <img 
              src={getHighResImage(actor.imageUrl) || actorFallbackImage} 
              alt={actor.name} 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = actorFallbackImage;
              }}
            />
          </div>
          <div>
            <h2 style={{ 
              fontSize: isMobile ? '1.5rem' : '2rem', 
              marginBottom: '10px',
              marginTop: isMobile ? '10px' : 0 
            }}>
              {actor.name}
            </h2>
            {/* The "Actor/Actress" line has been removed */}
          </div>
        </div>
        
        <div className="content-filter2" style={{
          display: 'flex',
          background: 'rgba(30, 30, 30, 0.7)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '30px',
          padding: '0',
          margin: '20px 0 30px',
          width: '100%',
          maxWidth: '300px',
          height: '50px',
          overflow: 'hidden',
          position: 'relative',
          marginLeft: isMobile ? 'auto' : '0',
          marginRight: isMobile ? 'auto' : '0'
        }}>
          <button 
            className={`filter-btn2 ${contentFilter === 'movies' ? 'active' : ''}`}
            onClick={() => setContentFilter('movies')}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              color: contentFilter === 'movies' ? 'white' : 'rgba(255, 255, 255, 0.7)',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.3s ease',
              background: contentFilter === 'movies' ? 'rgba(255, 255, 255, 0.15)' : 'transparent'
            }}
          >
            {t('movies')}
          </button>
          <div className="filter-divider2" style={{
            width: '1px',
            background: 'rgba(255, 255, 255, 0.1)',
            height: '100%'
          }}></div>
          <button 
            className={`filter-btn2 ${contentFilter === 'tv' ? 'active' : ''}`}
            onClick={() => setContentFilter('tv')}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              color: contentFilter === 'tv' ? 'white' : 'rgba(255, 255, 255, 0.7)',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.3s ease',
              background: contentFilter === 'tv' ? 'rgba(255, 255, 255, 0.15)' : 'transparent'
            }}
          >
            {t('tvShows')}
          </button>
        </div>
        
        <div className="filmography-section">
          {/* Filmography heading removed as requested */}
          
          {filmographyLoading ? (
            <div className="loading-indicator">{t('loadingFilmography')}</div>
          ) : actorFilmography ? (
            <div className="filmography-grid" style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(140px, 1fr))' : 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: '20px'
            }}>
              {filteredFilmography().length > 0 ? (
                filteredFilmography().map(credit => {
                  const year = credit.release_date ? new Date(credit.release_date).getFullYear() : 
                               (credit.first_air_date ? new Date(credit.first_air_date).getFullYear() : null);
                  const title = credit.title || credit.name || 'Untitled';
                  const poster = credit.poster_path ? `https://image.tmdb.org/t/p/w200${credit.poster_path}` : 'https://via.placeholder.com/200x300?text=No+Poster';
                  
                  return (
                    <div 
                      key={credit.id}
                      className="filmography-card"
                      onClick={() => onContentClick(credit, contentFilter === 'movies' ? 'movie' : 'tv')}
                    >
                      <div className="filmography-poster-container">
                        <img 
                          src={poster}
                          alt={title}
                          className="filmography-poster"
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/200x300?text=No+Poster';
                          }}
                        />
                      </div>
                      <div className="filmography-details">
                        <div className="filmography-meta-row">
                          {renderFilmographyRating(credit.vote_average)}
                          {year && (
                            <div className="filmography-meta-year">{year}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ 
                  gridColumn: '1 / -1', 
                  textAlign: 'center', 
                  padding: '20px',
                  color: '#aaa'
                }}>
                  {tDynamic('noFilmography', '{contentType}', getContentTypeLabel())}
                </div>
              )}
            </div>
          ) : (
            <p>{t('noData')}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActorModal;